import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { TournamentsAPI, PlayersAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import SiteHeader from "@/components/SiteHeader";
import QRCodeCard from "@/components/PlayerQRCode";
import { useAuth } from "@/contexts/AuthContext";
import OrganizerAssignCard from "@/components/OrganizerAssignCard";
import ImageInput from "@/components/ImageInput";
import PlayerPicker from "@/components/PlayerPicker";
import SectionOrderCard from "@/components/SectionOrderCard";
import AppearancePanel from "@/components/AppearancePanel";
import ScoringEditor from "@/components/ScoringEditor";
import { SPORTS, SPORT_IDS, getSport, sportTerms, effectiveScoring, computePoints, positionKeys, derivePositionStats, SCORING_LABELS } from "@/lib/sports";
import { LEAGUE_COLUMNS, computeLeagueStandings } from "@/lib/standings";
import { KO_FORMATS, ROUND_LABELS, ROUND_ORDER, generateBracket, resolveBracket, sourceLabel, scoredDownstream, finalRanking } from "@/lib/knockout";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, Copy, ArrowUp, ArrowDown } from "lucide-react";

const inputClass =
  "bg-[#0E0F13] border-white/10 text-white placeholder:text-zinc-600 focus-visible:border-[#D4AF37] focus-visible:ring-1 focus-visible:ring-[#D4AF37] rounded-md";

const SectionTitle = ({ children }) => (
  <h3 className="font-display text-2xl md:text-3xl font-bold uppercase tracking-tight pt-2">
    {children}
  </h3>
);

const Hint = ({ children }) => (
  <p className="text-zinc-500 font-body text-xs -mt-2">{children}</p>
);

const Field = ({ label, children, htmlFor }) => (
  <div className="space-y-1.5">
    <Label htmlFor={htmlFor} className="text-overline !text-zinc-400">
      {label}
    </Label>
    {children}
  </div>
);

const empty = {
  name: "",
  sport: "football",
  season: "",
  subtitle: "",
  cover_image_url: "",
  primary_color: "#D4AF37",
  teams: [],
  top_player_ids: [],
  top_players_custom: [],
  team_awards: [],
  highlights: [],
  gallery: [],
  sponsors: [],
  stats: {},
  appearance: {},
  scoring: {},
  standings_format: "sport",
  matches: [],
  knockout: null,
  section_order: [],
};

export default function AdminTournament() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const koEditWarnedRef = useRef(new Set());
  const isEdit = !!id && id !== "new";
  const [form, setForm] = useState(empty);
  const [allPlayers, setAllPlayers] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    PlayersAPI.list().then(setAllPlayers).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    TournamentsAPI.get(id)
      .then((d) => {
        const sc = effectiveScoring(d.sport || "football", d.scoring);
        const teams = (d.teams || []).map((t) => {
          const auto = computePoints(t, sc);
          return {
            ...t,
            points_manual: t.points_manual || (auto !== null && auto !== (t.points || 0)),
          };
        });
        setForm({
          ...empty,
          ...d,
          stats: { ...empty.stats, ...(d.stats || {}) },
          teams,
          top_player_ids: d.top_player_ids || [],
          gallery: d.gallery || [],
          sponsors: d.sponsors || [],
          standings_format: d.standings_format || "sport",
          matches: d.matches || [],
          knockout: d.knockout || null,
        });
      })
      .catch(() => toast.error("Tournoi introuvable"))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setStat = (k, v) =>
    setForm((f) => ({ ...f, stats: { ...f.stats, [k]: Number(v) || 0 } }));

  const updateTeam = (i, patch) =>
    setForm((f) => ({
      ...f,
      teams: f.teams.map((t, idx) => {
        if (idx !== i) return t;
        let next = { ...t, ...patch };
        if (f.standings_format === "league") {
          const keys = Object.keys(patch);
          if (["wins", "draws", "losses"].some((k) => keys.includes(k))) {
            next.matches_played =
              (next.wins || 0) + (next.draws || 0) + (next.losses || 0);
            next.points = 3 * (next.wins || 0) + (next.draws || 0);
          }
          if (["goals_for", "goals_against"].some((k) => keys.includes(k))) {
            next.goal_diff = (next.goals_for || 0) - (next.goals_against || 0);
          }
          return next;
        }
        const sc = effectiveScoring(f.sport, f.scoring);
        if (!sc) return next;
        if (getSport(f.sport).position_scoring && Object.keys(patch).some((k) => /^p\d+$/.test(k)))
          next = { ...next, ...derivePositionStats(next) };
        if ("points" in patch) return { ...next, points_manual: true };
        if (patch.points_manual === false)
          return { ...next, points_manual: false, points: computePoints(next, sc) };
        if (!next.points_manual && Object.keys(patch).some((k) => k in sc))
          return { ...next, points: computePoints(next, sc) };
        return next;
      }),
    }));

  const setScoring = (sc) =>
    setForm((f) => {
      const eff = effectiveScoring(f.sport, sc);
      return {
        ...f,
        scoring: sc,
        teams: eff
          ? f.teams.map((t) => (t.points_manual ? t : { ...t, points: computePoints(t, eff) }))
          : f.teams,
      };
    });

  const recomputeAllPoints = () =>
    setForm((f) => {
      const eff = effectiveScoring(f.sport, f.scoring);
      if (!eff) return f;
      return {
        ...f,
        teams: f.teams.map((t) => ({ ...t, points_manual: false, points: computePoints(t, eff) })),
      };
    });

  const sportPilots = allPlayers.filter((p) => (p.sport || "football") === form.sport);
  const linkPilot = (i, pid) => {
    if (pid === "none") return updateTeam(i, { player_id: null });
    const p = allPlayers.find((x) => x.id === pid);
    if (!p) return;
    updateTeam(i, {
      player_id: p.id,
      name: p.name,
      ...(p.hero_image_url ? { logo_url: p.hero_image_url } : {}),
    });
  };
  const addTeam = () =>
    setForm((f) => ({
      ...f,
      teams: [
        ...f.teams,
        {
          id: crypto.randomUUID(),
          name: "",
          logo_url: "",
          primary_color: "#0066FF",
          group: "",
          matches_played: 0,
          wins: 0,
          draws: 0,
          losses: 0,
          points: 0,
        },
      ],
    }));
  const removeTeam = (i) =>
    setForm((f) => {
      const teams = f.teams.filter((_, idx) => idx !== i);
      const removedId = f.teams[i]?.id;
      const matches = (f.matches || []).filter(
        (m) => m.home_id !== removedId && m.away_id !== removedId,
      );
      return {
        ...f,
        teams:
          f.standings_format === "league"
            ? computeLeagueStandings(teams, matches)
            : teams,
        matches,
      };
    });

  const duplicateTeam = (i) => {
    const srcName = form.teams[i]?.name || "Équipe";
    setForm((f) => {
      const src = f.teams[i];
      const copy = {
        ...src,
        id: crypto.randomUUID(),
        group: "",
        matches_played: 0,
        wins: 0,
        draws: 0,
        losses: 0,
        goals_for: 0,
        goals_against: 0,
        goal_diff: 0,
        points: 0,
        points_manual: false,
        extra: {},
      };
      const teams = [...f.teams];
      teams.splice(i + 1, 0, copy);
      return { ...f, teams };
    });
    toast.success(
      `${srcName} dupliquée — indiquez le groupe de la copie (ses stats repartent à zéro)`,
    );
  };

  const [tab, setTab] = useState("infos");
  const [manualRound, setManualRound] = useState("qf");
  const tabClass = (t) => (tab === t ? "space-y-8" : "hidden");
  const TABS = [
    { id: "infos", label: "1 · Infos" },
    { id: "equipes", label: "2 · Équipes & groupes" },
    { id: "matchs", label: "3 · Matchs" },
    { id: "finales", label: "4 · Phase finale" },
    { id: "medias", label: "5 · Médias & récompenses" },
  ];

  const isLeague = form.standings_format === "league";

  const setStandingsFormat = (fmt) => {
    if (fmt === form.standings_format) return;
    if (fmt === "league") {
      const hasManualData = form.teams.some(
        (t) => (t.matches_played || 0) > 0 || (t.points || 0) > 0,
      );
      if (
        hasManualData &&
        (form.matches || []).length === 0 &&
        !window.confirm(
          "Le classement sera désormais calculé automatiquement à partir des résultats de matchs (3 pts victoire, 1 pt nul, 0 défaite). Les valeurs saisies manuellement seront remplacées par le calcul. Les équipes, logos, groupes et toutes les autres données sont conservés. Continuer ?",
        )
      )
        return;
      setForm((f) => ({
        ...f,
        standings_format: "league",
        teams: computeLeagueStandings(f.teams, f.matches || []),
      }));
    } else {
      setForm((f) => ({ ...f, standings_format: "sport" }));
    }
  };

  const setMatches = (matches) =>
    setForm((f) => ({
      ...f,
      matches,
      teams: computeLeagueStandings(f.teams, matches),
    }));
  const addMatch = () =>
    setMatches([
      ...(form.matches || []),
      { id: crypto.randomUUID(), home_id: "", away_id: "", home_score: "", away_score: "", label: "" },
    ]);
  const updateMatch = (i, patch) =>
    setMatches(
      (form.matches || []).map((m, idx) => (idx === i ? { ...m, ...patch } : m)),
    );
  const removeMatch = (i) =>
    setMatches((form.matches || []).filter((_, idx) => idx !== i));

  const KO_DEFAULT = {
    format: "none",
    qualified_per_group: 2,
    third_place: true,
    classification: false,
    tiebreaker: "diff",
    matches: [],
  };
  const ko = form.knockout;
  const setKnockout = (patch) =>
    setForm((f) => ({ ...f, knockout: { ...(f.knockout || KO_DEFAULT), ...patch } }));

  const generateKO = () => {
    const cfg = ko || KO_DEFAULT;
    const matches = generateBracket({
      teams: form.teams,
      format: cfg.format,
      qualifiedPerGroup: cfg.qualified_per_group,
      thirdPlace: cfg.third_place,
      classification: cfg.classification,
    });
    if (matches === null) {
      toast.error("Pas assez d'équipes qualifiées pour ce format");
      return;
    }
    const existing = cfg.matches || [];
    const hasScores = existing.some(
      (m) => (m.home_score ?? "") !== "" || (m.away_score ?? "") !== "",
    );
    if (
      hasScores &&
      !window.confirm(
        "Régénérer le tableau effacera les scores déjà saisis dans les phases finales. Les matchs de groupes ne sont pas touchés. Continuer ?",
      )
    )
      return;
    setKnockout({ matches });
    toast.success("Tableau des phases finales généré");
  };

  const updateKOMatch = (matchId, patch) => {
    if ("home_score" in patch || "away_score" in patch) {
      const impacted = scoredDownstream(ko?.matches || [], matchId);
      if (impacted.length > 0 && !koEditWarnedRef.current.has(matchId)) {
        koEditWarnedRef.current.add(matchId);
        toast.warning(
          `Attention : ${impacted.map((m) => m.label).join(", ")} ${impacted.length > 1 ? "ont" : "a"} déjà un score. Les équipes qualifiées seront recalculées automatiquement — vérifiez la suite du tableau.`,
          { duration: 7000 },
        );
      }
    }
    setKnockout({
      matches: (ko?.matches || []).map((m) => (m.id === matchId ? { ...m, ...patch } : m)),
    });
  };

  const addKOMatch = (round) => {
    const cfg = ko || KO_DEFAULT;
    const count = (cfg.matches || []).filter((m) => m.round === round).length;
    setKnockout({
      format: cfg.format === "none" ? (round === "sf" || round === "final" || round === "third" ? "sf" : "qf") : cfg.format,
      matches: [
        ...(cfg.matches || []),
        {
          id: crypto.randomUUID(),
          round,
          slot: count + 1,
          label: count ? `${ROUND_LABELS[round]} ${count + 1}` : ROUND_LABELS[round],
          home_source: { type: "team", team_id: "" },
          away_source: { type: "team", team_id: "" },
          home_score: null,
          away_score: null,
          date: "",
          venue: "",
        },
      ],
    });
  };

  const removeKOMatch = (matchId) =>
    setKnockout({ matches: (ko?.matches || []).filter((m) => m.id !== matchId) });

  const overrideList = ko?.final_ranking_override || [];
  const setOverrideList = (list) =>
    setKnockout({
      final_ranking_override: list.map((r, i) => ({ position: i + 1, team_id: r.team_id })),
    });
  const startOverride = () => {
    const computed = finalRanking(
      ko?.matches?.length ? resolveBracket(form.teams, ko) : [],
    );
    setOverrideList(
      computed.length ? computed.map((r) => ({ team_id: r.team.id })) : [{ team_id: "" }],
    );
  };
  const setOverrideRow = (i, team_id) =>
    setOverrideList(overrideList.map((r, idx) => (idx === i ? { ...r, team_id } : r)));
  const addOverrideRow = () => setOverrideList([...overrideList, { team_id: "" }]);
  const removeOverrideRow = (i) =>
    setOverrideList(overrideList.filter((_, idx) => idx !== i));
  const moveOverride = (i, dir) => {
    const list = [...overrideList];
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    setOverrideList(list);
  };
  const clearOverride = () => setKnockout({ final_ranking_override: [] });

  const [applyingUgs, setApplyingUgs] = useState(false);
  const applyUgs = async () => {
    if (
      !window.confirm(
        "Remplacer les classements, matchs et phases finales de cette page par les résultats officiels UGS vérifiés ? Le QR code, le lien, le design et les récompenses ne changent pas.",
      )
    )
      return;
    setApplyingUgs(true);
    try {
      const r = await TournamentsAPI.applyUgsOfficial();
      toast.success(
        `Résultats officiels appliqués : ${r.teams} équipes, ${r.matches} matchs, ${r.ko} matchs à élimination`,
      );
      window.location.reload();
    } catch {
      toast.error("Échec de l'application des résultats officiels");
    } finally {
      setApplyingUgs(false);
    }
  };

  const koResolved = ko?.matches?.length ? resolveBracket(form.teams, ko) : [];
  const koById = Object.fromEntries((ko?.matches || []).map((m) => [m.id, m]));
  const koByRound = {};
  koResolved.forEach((m) => {
    (koByRound[m.round] ||= []).push(m);
  });

  const togglePlayer = (pid) =>
    setForm((f) => ({
      ...f,
      top_player_ids: f.top_player_ids.includes(pid)
        ? f.top_player_ids.filter((x) => x !== pid)
        : [...f.top_player_ids, pid],
    }));

  const updateHighlight = (i, patch) =>
    setForm((f) => ({
      ...f,
      highlights: f.highlights.map((h, idx) => (idx === i ? { ...h, ...patch } : h)),
    }));
  const addHighlight = () =>
    setForm((f) => ({
      ...f,
      highlights: [
        ...f.highlights,
        { id: crypto.randomUUID(), title: "", type: "youtube", url: "" },
      ],
    }));
  const removeHighlight = (i) =>
    setForm((f) => ({ ...f, highlights: f.highlights.filter((_, idx) => idx !== i) }));

  const addItem = (key, item) =>
    setForm((f) => ({ ...f, [key]: [...(f[key] || []), { id: crypto.randomUUID(), ...item }] }));
  const updateItem = (key, i, patch) =>
    setForm((f) => ({
      ...f,
      [key]: f[key].map((x, idx) => (idx === i ? { ...x, ...patch } : x)),
    }));
  const removeItem = (key, i) =>
    setForm((f) => ({ ...f, [key]: f[key].filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setTab("infos");
      toast.error("Indiquez le nom du tournoi (onglet Infos)");
      return;
    }
    if (!form.name?.trim()) {
      toast.error("Le nom du tournoi est requis");
      return;
    }
    setSaving(true);
    try {
      const num = (v) => (v === "" || v == null ? null : Number(v));
      const sanitizeScores = (arr) =>
        (arr || []).map((m) => ({
          ...m,
          home_score: num(m.home_score),
          away_score: num(m.away_score),
          ...("home_pens" in m || "away_pens" in m
            ? { home_pens: num(m.home_pens), away_pens: num(m.away_pens) }
            : {}),
        }));
      const payload = {
        ...form,
        matches: sanitizeScores(form.matches),
        knockout: form.knockout
          ? { ...form.knockout, matches: sanitizeScores(form.knockout.matches) }
          : null,
      };
      const result = isEdit
        ? await TournamentsAPI.update(id, payload)
        : await TournamentsAPI.create(payload);
      toast.success(isEdit ? "Tournoi mis à jour" : "Tournoi créé");
      navigate(`/tournament/${result.slug}`);
    } catch {
      toast.error("Échec de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm("Supprimer ce tournoi ?")) return;
    try {
      await TournamentsAPI.remove(id);
      toast.success("Tournoi supprimé");
      navigate("/tournaments");
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  if (loading)
    return (
      <div className="min-h-screen bg-[#050505] text-zinc-500 font-body flex items-center justify-center">
        Chargement...
      </div>
    );

  const terms = sportTerms(form.sport);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <SiteHeader />
      <div className="max-w-3xl mx-auto w-full px-4 md:px-8 py-10 md:py-16">
        <Link
          to="/tournaments"
          data-testid="admin-tournament-back"
          className="inline-flex items-center gap-2 text-zinc-500 hover:text-white transition-colors text-xs uppercase tracking-[0.25em] font-body mb-6"
        >
          <ArrowLeft className="w-3 h-3" /> Retour
        </Link>

        <h1 className="font-display text-4xl md:text-6xl font-black uppercase tracking-tighter leading-none mb-2">
          {isEdit ? "Éditer" : "Nouveau"} <span style={{ color: "#D4AF37" }}>tournoi</span>
        </h1>
        <p className="text-zinc-400 font-body mb-10">
          Le tournoi a son QR code à graver sur le trophée. Tu rentres manuellement le classement.
        </p>

        {tab === "infos" && isEdit && form.slug && isAdmin && (
          <div className="mb-10">
            <QRCodeCard
              url={`${window.location.origin}/tournament/${form.slug}`}
              title={form.name}
              subtitle={form.season ? `Saison ${form.season}` : form.subtitle}
              fileBase={`tournament-${form.slug}`}
              primaryColor={form.primary_color || "#D4AF37"}
            />
          </div>
        )}

        {tab === "infos" && isEdit && (
          <OrganizerAssignCard
            organizerId={form.organizer_id}
            onAssign={async (orgId) => {
              const updated = await TournamentsAPI.assignOrganizer(id, orgId);
              setForm((f) => ({
                ...f,
                organizer_id: updated.organizer_id,
                organizer_email: updated.organizer_email,
              }));
            }}
          />
        )}

        {tab === "infos" && isEdit && isAdmin && form.slug === "agne-ugs" && (
          <div className="mb-10 bg-[#111216] border border-[#D4AF37]/30 rounded-xl p-5">
            <p className="font-display font-bold uppercase tracking-wide text-sm mb-1">
              Résultats officiels <span style={{ color: "#D4AF37" }}>UGS</span>
            </p>
            <p className="text-xs text-zinc-500 font-body mb-3">
              Applique les résultats vérifiés du site officiel (2 journées, phases
              finales, tirs au but) sur cette page. Le QR code, le lien et les
              récompenses ne changent pas.
            </p>
            <Button
              type="button"
              data-testid="apply-ugs-official"
              disabled={applyingUgs}
              onClick={applyUgs}
              className="h-9 text-xs font-bold text-black hover:opacity-90"
              style={{ background: "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)" }}
            >
              {applyingUgs ? "Application…" : "Appliquer les résultats officiels"}
            </Button>
          </div>
        )}

        <form onSubmit={submit} className="space-y-8" data-testid="admin-tournament-form">
          <div className="sticky top-[65px] z-20 -mx-4 px-4 py-2 bg-[#050505]/95 backdrop-blur border-b border-white/10">
            <div className="flex flex-wrap gap-1.5">
              {TABS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  data-testid={`tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className={`whitespace-nowrap px-3.5 py-2 rounded-full text-[11px] uppercase tracking-[0.14em] font-bold border transition-colors ${
                    tab === t.id
                      ? "border-[#D4AF37] text-black"
                      : "border-white/10 text-zinc-400 hover:text-white"
                  }`}
                  style={
                    tab === t.id
                      ? { background: "linear-gradient(180deg,#FFE9A8 0%,#D4AF37 60%,#B8941F 100%)" }
                      : {}
                  }
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className={tabClass("infos")}>
          <SectionTitle>Sport</SectionTitle>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
            {SPORT_IDS.map((s) => {
              const sport = SPORTS[s];
              const active = (form.sport || "football") === s;
              return (
                <button
                  type="button"
                  key={s}
                  onClick={() => setForm((f) => ({ ...f, sport: s, scoring: {} }))}
                  data-testid={`t-select-sport-${s}`}
                  data-active={active}
                  className="metal-pill px-3 py-2.5 rounded-md text-[10px] uppercase tracking-[0.22em] font-bold"
                  style={{ "--metal-accent": sport.color }}
                >
                  {sport.label}
                </button>
              );
            })}
          </div>

          <SectionTitle>Identité</SectionTitle>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Nom du tournoi *" htmlFor="t-name">
              <Input
                id="t-name"
                data-testid="t-input-name"
                className={inputClass}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="Emergent Cup"
              />
            </Field>
            <Field label="Saison" htmlFor="t-season">
              <Input
                id="t-season"
                data-testid="t-input-season"
                className={inputClass}
                value={form.season}
                onChange={(e) => set("season", e.target.value)}
                placeholder="2025"
              />
            </Field>
            <Field label="Sous-titre" htmlFor="t-subtitle">
              <Input
                id="t-subtitle"
                className={inputClass}
                value={form.subtitle}
                onChange={(e) => set("subtitle", e.target.value)}
                placeholder="Plus qu'un trophée, une expérience digitale"
              />
            </Field>
            <Field label="Couleur d'accent" htmlFor="t-color">
              <div className="flex gap-2 items-center">
                <Input
                  id="t-color"
                  type="color"
                  className={`${inputClass} h-10 w-16 p-1 cursor-pointer`}
                  value={form.primary_color}
                  onChange={(e) => set("primary_color", e.target.value)}
                />
                <Input
                  className={inputClass}
                  value={form.primary_color}
                  onChange={(e) => set("primary_color", e.target.value)}
                  placeholder="#D4AF37"
                />
              </div>
            </Field>
            <Field label="Couleur du nom du tournoi" htmlFor="t-title-color">
              <div className="flex gap-2 items-center">
                <Input
                  id="t-title-color"
                  type="color"
                  className={`${inputClass} h-10 w-16 p-1 cursor-pointer`}
                  value={form.appearance?.title_color || "#D4AF37"}
                  onChange={(e) => set("appearance", { ...form.appearance, title_color: e.target.value })}
                  data-testid="t-title-color"
                />
                <Input
                  className={inputClass}
                  value={form.appearance?.title_color || ""}
                  onChange={(e) => set("appearance", { ...form.appearance, title_color: e.target.value })}
                  placeholder="Vide = dégradé doré"
                />
                {form.appearance?.title_color && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-zinc-400 hover:text-white shrink-0 px-2"
                    onClick={() => set("appearance", { ...form.appearance, title_color: "" })}
                    data-testid="t-title-color-reset"
                  >
                    Réinit
                  </Button>
                )}
              </div>
            </Field>
            <Field label="Couleur du sous-titre" htmlFor="t-subtitle-color">
              <div className="flex gap-2 items-center">
                <Input
                  id="t-subtitle-color"
                  type="color"
                  className={`${inputClass} h-10 w-16 p-1 cursor-pointer`}
                  value={form.appearance?.subtitle_color || "#d4d4d8"}
                  onChange={(e) => set("appearance", { ...form.appearance, subtitle_color: e.target.value })}
                  data-testid="t-subtitle-color"
                />
                <Input
                  className={inputClass}
                  value={form.appearance?.subtitle_color || ""}
                  onChange={(e) => set("appearance", { ...form.appearance, subtitle_color: e.target.value })}
                  placeholder="Vide = gris clair par défaut"
                />
                {form.appearance?.subtitle_color && (
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-zinc-400 hover:text-white shrink-0 px-2"
                    onClick={() => set("appearance", { ...form.appearance, subtitle_color: "" })}
                    data-testid="t-subtitle-color-reset"
                  >
                    Réinit
                  </Button>
                )}
              </div>
            </Field>
            <Field label="Photo de couverture" htmlFor="t-cover">
              <ImageInput
                value={form.cover_image_url}
                onChange={(v) => set("cover_image_url", v)}
                testId="img-tournament-cover"
                aspect="wide"
                placeholder="https://..."
              />
            </Field>
          </div>

          <SectionTitle>Design & Couleurs</SectionTitle>
          <p className="text-zinc-500 font-body text-sm -mt-4">
            Personnalise entièrement les couleurs de la page publique. Tu peux extraire
            automatiquement une palette depuis la photo de couverture, puis ajuster manuellement.
          </p>
          <AppearancePanel
            appearance={form.appearance || {}}
            onChange={(v) => set("appearance", v)}
            imageUrl={form.cover_image_url}
            accent={form.primary_color}
            onAccent={(v) => set("primary_color", v)}
            testIdPrefix="t-ap"
          />

          <SectionTitle>Stats du tournoi</SectionTitle>
          <p className="text-zinc-500 font-body text-sm -mt-4">
            Stats adaptées au sport sélectionné.
          </p>
          <div
            className="grid gap-4"
            style={{
              gridTemplateColumns: `repeat(${Math.min(getSport(form.sport).tournament_stats.length, 4)}, minmax(0, 1fr))`,
            }}
          >
            {getSport(form.sport).tournament_stats.map((s) => (
              <Field key={s.key} label={s.label} htmlFor={`t-stat-${s.key}`}>
                <Input
                  id={`t-stat-${s.key}`}
                  data-testid={`t-stat-${s.key}`}
                  type="number"
                  className={inputClass}
                  placeholder="0"
                  value={form.stats[s.key] || ""}
                  onChange={(e) => setStat(s.key, e.target.value)}
                />
              </Field>
            ))}
          </div>

          </div>

          <div className={tabClass("equipes")}>
          <SectionTitle>{terms.entities} & Classement</SectionTitle>
          <div className="bg-[#0A0B0E] border border-white/5 rounded-md p-3">
            <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 block mb-2">
              Format du classement
            </Label>
            <div className="grid sm:grid-cols-2 gap-2">
              <button
                type="button"
                data-testid="standings-format-sport"
                onClick={() => setStandingsFormat("sport")}
                className={`rounded-md border px-3 py-2.5 text-left transition-colors ${
                  !isLeague
                    ? "border-[#D4AF37]/60 bg-[#D4AF37]/10"
                    : "border-white/10 bg-white/[0.03] hover:border-white/30"
                }`}
              >
                <div className="font-display uppercase tracking-tight font-semibold text-sm">
                  Classement du sport
                </div>
                <div className="text-[11px] text-zinc-500 font-body">
                  Colonnes selon le sport, saisie manuelle des valeurs
                </div>
              </button>
              <button
                type="button"
                data-testid="standings-format-league"
                onClick={() => setStandingsFormat("league")}
                className={`rounded-md border px-3 py-2.5 text-left transition-colors ${
                  isLeague
                    ? "border-[#D4AF37]/60 bg-[#D4AF37]/10"
                    : "border-white/10 bg-white/[0.03] hover:border-white/30"
                }`}
              >
                <div className="font-display uppercase tracking-tight font-semibold text-sm">
                  Classement général de championnat
                </div>
                <div className="text-[11px] text-zinc-500 font-body">
                  M · V · N · D · BP · BC · +/- · PTS — calculé automatiquement des matchs (3/1/0)
                </div>
              </button>
            </div>
          </div>
          {!isLeague && (
            <ScoringEditor
              sport={form.sport}
              scoring={form.scoring}
              onChange={setScoring}
              onRecomputeAll={recomputeAllPoints}
              testIdPrefix="t-scoring"
            />
          )}
          <div className="space-y-3">
            {form.teams.map((team, i) => (
              <div
                key={team.id || i}
                className="bg-[#0A0B0E] border border-white/5 rounded-md p-3 space-y-2"
                data-testid={`team-row-form-${i}`}
              >
                <div className="grid sm:grid-cols-[auto,1fr,auto,40px] gap-3 items-start">
                  <ImageInput
                    value={team.logo_url || ""}
                    onChange={(v) => updateTeam(i, { logo_url: v })}
                    testId={`img-team-logo-${i}`}
                    size="sm"
                    placeholder="URL logo"
                  />
                  <div className="space-y-2">
                    <Input
                      className={inputClass}
                      placeholder={terms.namePlaceholder}
                      value={team.name}
                      onChange={(e) => updateTeam(i, { name: e.target.value })}
                      data-testid={`t-team-name-${i}`}
                    />
                    <Input
                      className={`${inputClass} h-8 text-xs`}
                      placeholder="Groupe (ex: Groupe 1) — facultatif"
                      value={team.group || ""}
                      onChange={(e) => updateTeam(i, { group: e.target.value })}
                      data-testid={`t-group-${i}`}
                    />
                    {terms.individual && sportPilots.length > 0 && (
                      <Select
                        value={team.player_id || "none"}
                        onValueChange={(v) => linkPilot(i, v)}
                      >
                        <SelectTrigger
                          className={`${inputClass} h-8 text-xs`}
                          data-testid={`t-pilot-link-${i}`}
                        >
                          <SelectValue placeholder="Lier à une fiche" />
                        </SelectTrigger>
                        <SelectContent className="bg-[#111216] border-white/10 text-white">
                          <SelectItem value="none">Saisie libre (non liée)</SelectItem>
                          {sportPilots.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <Input
                    type="color"
                    className={`${inputClass} h-10 w-14 p-1 cursor-pointer`}
                    value={team.primary_color || "#0066FF"}
                    onChange={(e) =>
                      updateTeam(i, { primary_color: e.target.value })
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    title="Dupliquer dans un autre groupe"
                    onClick={() => duplicateTeam(i)}
                    className="text-zinc-400 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10"
                    data-testid={`duplicate-team-${i}`}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => removeTeam(i)}
                    className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                    data-testid={`remove-team-${i}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                {!isLeague && positionKeys(form.sport).length > 0 && (
                  <div className="pt-1">
                    <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                      Arrivées par position (nombre de courses terminées à chaque place)
                    </Label>
                    <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5 mt-1">
                      {positionKeys(form.sport).map((k) => (
                        <div key={k}>
                          <div className="text-[9px] uppercase tracking-[0.12em] text-zinc-600 text-center">
                            {SCORING_LABELS[k] || k}
                          </div>
                          <Input
                            type="number"
                            min="0"
                            className={`${inputClass} h-8 px-1 text-center text-xs`}
                            placeholder="0"
                            value={team[k] || ""}
                            onChange={(e) => updateTeam(i, { [k]: Number(e.target.value) || 0 })}
                            data-testid={`t-pos-${k}-${i}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {isLeague ? (
                  <div
                    className="grid gap-2"
                    style={{
                      gridTemplateColumns: `repeat(${LEAGUE_COLUMNS.length}, minmax(0, 1fr))`,
                    }}
                    data-testid={`league-stats-${i}`}
                  >
                    {LEAGUE_COLUMNS.map(({ key, label }) => {
                      const derived = ["matches_played", "points", "goal_diff"].includes(key);
                      return (
                        <div key={key}>
                          <Label
                            className={`text-[10px] uppercase tracking-[0.18em] block text-center ${key === "points" ? "text-[#D4AF37]" : "text-zinc-500"}`}
                            title={derived ? "Calculé automatiquement, modifiable" : undefined}
                          >
                            {label}
                          </Label>
                          <Input
                            type="number"
                            className={`${inputClass} h-9 px-1 text-center text-sm ${key === "points" ? "text-[#D4AF37] font-bold" : ""}`}
                            placeholder="0"
                            value={team[key] || ""}
                            onChange={(e) =>
                              updateTeam(i, { [key]: Number(e.target.value) || 0 })
                            }
                            data-testid={`t-league-${key}-${i}`}
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                <div
                  className="grid gap-2"
                  style={{
                    gridTemplateColumns: `repeat(${getSport(form.sport).standings_columns.length}, minmax(0, 1fr))`,
                  }}
                >
                  {getSport(form.sport).standings_columns.map(({ key, label }) => {
                    const isPts = key === "points";
                    const hasScoring = !!effectiveScoring(form.sport, form.scoring);
                    return (
                      <div key={key}>
                        <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                          {label}
                          {isPts && hasScoring && team.points_manual && (
                            <span className="text-amber-400 normal-case ml-1" title="Points saisis manuellement">manuel</span>
                          )}
                        </Label>
                        <Input
                          type="number"
                          className={inputClass}
                          placeholder="0"
                          value={team[key] || ""}
                          onChange={(e) => updateTeam(i, { [key]: Number(e.target.value) || 0 })}
                          data-testid={isPts ? `t-points-${i}` : undefined}
                        />
                        {isPts && hasScoring && team.points_manual && (
                          <button
                            type="button"
                            onClick={() => updateTeam(i, { points_manual: false })}
                            className="mt-1 text-[9px] uppercase tracking-[0.14em] text-[#D4AF37] hover:underline"
                            data-testid={`t-points-auto-${i}`}
                          >
                            Auto
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              data-testid="add-team"
              onClick={addTeam}
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> {terms.add}
            </Button>
          </div>
          </div>

          <div className={tabClass("matchs")}>
          {!isLeague && (
            <p className="text-zinc-500 font-body text-sm bg-[#111216] border border-white/10 rounded-xl p-5">
              La saisie de matchs est disponible avec le format «&nbsp;Classement
              général de championnat&nbsp;» — choisissez-le dans l'onglet
              «&nbsp;Équipes & groupes&nbsp;». En format classique, les valeurs du
              classement se saisissent directement sur chaque équipe.
            </p>
          )}
          {isLeague && (
            <>
              <SectionTitle>Matchs & résultats</SectionTitle>
              <p className="text-zinc-500 font-body text-sm -mt-4">
                Saisissez les résultats : le classement se met à jour automatiquement
                (victoire 3 pts, nul 1 pt, défaite 0 pt). Vous pouvez aussi saisir les
                valeurs directement dans les équipes ci-dessus — attention, l'ajout ou la
                modification d'un match recalcule tout le classement à partir des matchs.
              </p>
              <div className="space-y-2">
                {(form.matches || []).map((m, i) => (
                  <div
                    key={m.id || i}
                    className="bg-[#0A0B0E] border border-white/5 rounded-md p-2.5 space-y-2"
                    data-testid={`match-row-${i}`}
                  >
                    <div className="grid grid-cols-[1fr,52px,12px,52px,1fr,auto] gap-2 items-center">
                    <Select
                      value={m.home_id || ""}
                      onValueChange={(v) => updateMatch(i, { home_id: v })}
                    >
                      <SelectTrigger
                        className={`${inputClass} h-9 text-xs`}
                        data-testid={`match-home-${i}`}
                      >
                        <SelectValue placeholder="Domicile" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111216] border-white/10 text-white">
                        {form.teams
                          .filter((t) => t.name && t.id !== m.away_id)
                          .map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.name}
                              {t.group ? ` (${t.group})` : ""}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min="0"
                      className={`${inputClass} h-9 px-1 text-center`}
                      placeholder="-"
                      value={m.home_score ?? ""}
                      onChange={(e) => updateMatch(i, { home_score: e.target.value })}
                      data-testid={`match-home-score-${i}`}
                    />
                    <div className="text-center text-zinc-600 font-display">–</div>
                    <Input
                      type="number"
                      min="0"
                      className={`${inputClass} h-9 px-1 text-center`}
                      placeholder="-"
                      value={m.away_score ?? ""}
                      onChange={(e) => updateMatch(i, { away_score: e.target.value })}
                      data-testid={`match-away-score-${i}`}
                    />
                    <Select
                      value={m.away_id || ""}
                      onValueChange={(v) => updateMatch(i, { away_id: v })}
                    >
                      <SelectTrigger
                        className={`${inputClass} h-9 text-xs`}
                        data-testid={`match-away-${i}`}
                      >
                        <SelectValue placeholder="Extérieur" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111216] border-white/10 text-white">
                        {form.teams
                          .filter((t) => t.name && t.id !== m.home_id)
                          .map((t) => (
                            <SelectItem key={t.id} value={t.id}>
                              {t.name}
                              {t.group ? ` (${t.group})` : ""}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => removeMatch(i)}
                      className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10 h-9 w-9 p-0"
                      data-testid={`remove-match-${i}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        className={`${inputClass} h-8 text-xs`}
                        placeholder="Date / heure (ex: 25 oct. · 10h00)"
                        value={m.date || ""}
                        onChange={(e) => updateMatch(i, { date: e.target.value })}
                        data-testid={`match-date-${i}`}
                      />
                      <Input
                        className={`${inputClass} h-8 text-xs`}
                        placeholder="Salle / terrain"
                        value={m.venue || ""}
                        onChange={(e) => updateMatch(i, { venue: e.target.value })}
                        data-testid={`match-venue-${i}`}
                      />
                    </div>
                  </div>
                ))}
                <Button
                  type="button"
                  variant="outline"
                  data-testid="add-match"
                  onClick={addMatch}
                  className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
                >
                  <Plus className="w-4 h-4 mr-2" /> Ajouter un match
                </Button>
              </div>
            </>
          )}
          </div>

          <div className={tabClass("finales")}>
          {!isLeague && (
            <p className="text-zinc-500 font-body text-sm bg-[#111216] border border-white/10 rounded-xl p-5">
              La phase finale est disponible avec le format «&nbsp;Classement
              général de championnat&nbsp;» — choisissez-le dans l'onglet
              «&nbsp;Équipes & groupes&nbsp;».
            </p>
          )}
          {isLeague && (
            <>
              <SectionTitle>Phases finales</SectionTitle>
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 block mb-1">
                      Format des phases finales
                    </Label>
                    <Select
                      value={ko?.format || "none"}
                      onValueChange={(v) => {
                        setKnockout({ format: v });
                        if (
                          v !== "none" &&
                          (ko?.matches || []).length &&
                          (ko?.matches || [])[0]?.round !== { sf: "sf", qf: "qf", r16: "r16" }[v]
                        ) {
                          toast.info(
                            "Format modifié — cliquez sur « Régénérer le tableau » pour l'appliquer",
                          );
                        }
                      }}
                    >
                      <SelectTrigger className={`${inputClass} h-10`} data-testid="ko-format">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#111216] border-white/10 text-white">
                        {KO_FORMATS.map((f) => (
                          <SelectItem key={f.value} value={f.value}>
                            {f.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {(ko?.format || "none") !== "none" && (
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 block mb-1">
                        Qualifiés par groupe
                      </Label>
                      <Select
                        value={String(ko?.qualified_per_group || 2)}
                        onValueChange={(v) => setKnockout({ qualified_per_group: Number(v) })}
                      >
                        <SelectTrigger className={`${inputClass} h-10`} data-testid="ko-qualified">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#111216] border-white/10 text-white">
                          {[1, 2, 3, 4].map((n) => (
                            <SelectItem key={n} value={String(n)}>
                              {n} par groupe
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>

                {(ko?.format || "none") !== "none" && (
                  <>
                    <div className="grid sm:grid-cols-3 gap-3">
                      <div>
                        <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 block mb-1">
                          Départage
                        </Label>
                        <Select
                          value={ko?.tiebreaker || "diff"}
                          onValueChange={(v) => setKnockout({ tiebreaker: v })}
                        >
                          <SelectTrigger className={`${inputClass} h-10`} data-testid="ko-tiebreaker">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#111216] border-white/10 text-white">
                            <SelectItem value="diff">Pts, puis différence, puis buts</SelectItem>
                            <SelectItem value="goals">Pts, puis buts marqués, puis différence</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <label className="flex items-center gap-2.5 bg-[#0A0B0E] border border-white/5 rounded-md px-3 h-10 self-end cursor-pointer">
                        <Switch
                          checked={ko?.third_place ?? true}
                          onCheckedChange={(v) => setKnockout({ third_place: v })}
                          className="data-[state=checked]:bg-[#D4AF37] data-[state=unchecked]:bg-white/20"
                          data-testid="ko-third-place"
                        />
                        <span className="text-xs text-zinc-300 font-body">Petite finale (3e place)</span>
                      </label>
                      {["qf", "r16"].includes(ko?.format) && (
                        <label className="flex items-center gap-2.5 bg-[#0A0B0E] border border-white/5 rounded-md px-3 py-2 min-h-10 self-end cursor-pointer">
                          <Switch
                            checked={ko?.classification ?? false}
                            onCheckedChange={(v) => setKnockout({ classification: v })}
                            className="data-[state=checked]:bg-[#D4AF37] data-[state=unchecked]:bg-white/20"
                            data-testid="ko-classification"
                          />
                          <span className="text-xs text-zinc-300 font-body">Classement 5e-8e</span>
                        </label>
                      )}
                    </div>

                    <Button
                      type="button"
                      data-testid="ko-generate"
                      onClick={generateKO}
                      className="bg-white/10 hover:bg-white/20 text-white border border-white/15 rounded-md"
                    >
                      {ko?.matches?.length ? "Régénérer le tableau" : "Générer le tableau"}
                    </Button>

                    <div className="bg-[#0A0B0E] border border-white/5 rounded-md p-3">
                      <Label className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 block mb-2">
                        Mode personnalisé — ajouter un match librement
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        <Select value={manualRound} onValueChange={setManualRound}>
                          <SelectTrigger
                            className={`${inputClass} h-10 w-64`}
                            data-testid="ko-manual-round"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#111216] border-white/10 text-white">
                            {["r16", "qf", "sf", "third", "c_sf", "c5", "c7", "final"].map((r) => (
                              <SelectItem key={r} value={r}>
                                {ROUND_LABELS[r]}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="outline"
                          data-testid="ko-manual-add"
                          onClick={() => addKOMatch(manualRound)}
                          className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white h-10"
                        >
                          <Plus className="w-4 h-4 mr-2" /> Ajouter ce match
                        </Button>
                      </div>
                      <p className="text-[11px] text-zinc-600 font-body mt-2">
                        Choisissez ensuite les deux équipes sur le match ajouté — idéal
                        pour reproduire un règlement particulier sans tableau automatique.
                      </p>
                    </div>

                    {koResolved.length > 0 && (
                      <div className="space-y-5">
                        <p className="text-[11px] text-zinc-500 font-body">
                          Les équipes avancent automatiquement selon les résultats. En cas
                          d'égalité, des champs «&nbsp;Tirs au but&nbsp;» apparaissent pour
                          départager le vainqueur — affichés 3(2) - 3(1) sur la page publique.
                        </p>
                        {ROUND_ORDER.filter((r) => koByRound[r]?.length).map((round) => (
                          <div key={round}>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold mb-2">
                              {ROUND_LABELS[round]}
                            </div>
                            <div className="space-y-2">
                              {koByRound[round].map((m) => (
                                <div
                                  key={m.id}
                                  className="bg-[#0A0B0E] border border-white/5 rounded-md p-2.5 space-y-2"
                                  data-testid={`ko-match-${m.label}`}
                                >
                                  <div className="grid grid-cols-[1fr,52px,12px,52px,1fr] gap-2 items-center">
                                    <div className="text-xs font-body text-right truncate text-zinc-200">
                                      {m.home_team?.name || (
                                        <span className="text-zinc-600 italic">
                                          {sourceLabel(m.home_source, koById)}
                                        </span>
                                      )}
                                    </div>
                                    <Input
                                      type="number"
                                      min="0"
                                      className={`${inputClass} h-9 px-1 text-center`}
                                      placeholder="-"
                                      value={m.home_score ?? ""}
                                      onChange={(e) => updateKOMatch(m.id, { home_score: e.target.value })}
                                      data-testid={`ko-home-score-${m.label}`}
                                    />
                                    <div className="text-center text-zinc-600 font-display">–</div>
                                    <Input
                                      type="number"
                                      min="0"
                                      className={`${inputClass} h-9 px-1 text-center`}
                                      placeholder="-"
                                      value={m.away_score ?? ""}
                                      onChange={(e) => updateKOMatch(m.id, { away_score: e.target.value })}
                                      data-testid={`ko-away-score-${m.label}`}
                                    />
                                    <div className="text-xs font-body truncate text-zinc-200">
                                      {m.away_team?.name || (
                                        <span className="text-zinc-600 italic">
                                          {sourceLabel(m.away_source, koById)}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  {(m.home_score ?? "") !== "" &&
                                    (m.away_score ?? "") !== "" &&
                                    Number(m.home_score) === Number(m.away_score) && (
                                      <div className="grid grid-cols-[1fr,52px,12px,52px,1fr] gap-2 items-center">
                                        <div className="text-[10px] uppercase tracking-[0.15em] text-[#D4AF37] text-right">
                                          Tirs au but
                                        </div>
                                        <Input
                                          type="number"
                                          min="0"
                                          className={`${inputClass} h-8 px-1 text-center text-xs border-[#D4AF37]/30`}
                                          placeholder="-"
                                          value={m.home_pens ?? ""}
                                          onChange={(e) => updateKOMatch(m.id, { home_pens: e.target.value })}
                                          data-testid={`ko-home-pens-${m.label}`}
                                        />
                                        <div className="text-center text-zinc-600 font-display text-xs">–</div>
                                        <Input
                                          type="number"
                                          min="0"
                                          className={`${inputClass} h-8 px-1 text-center text-xs border-[#D4AF37]/30`}
                                          placeholder="-"
                                          value={m.away_pens ?? ""}
                                          onChange={(e) => updateKOMatch(m.id, { away_pens: e.target.value })}
                                          data-testid={`ko-away-pens-${m.label}`}
                                        />
                                        <div className="text-[10px] text-zinc-600 font-body">
                                          départagent le vainqueur
                                        </div>
                                      </div>
                                    )}
                                  <div className="grid grid-cols-[auto,1fr,1fr,auto] gap-2 items-center">
                                    <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-600 font-bold px-1">
                                      {m.label}
                                    </span>
                                    <Input
                                      className={`${inputClass} h-8 text-xs`}
                                      placeholder="Date / heure (ex: 15 juin · 14h00)"
                                      value={m.date || ""}
                                      onChange={(e) => updateKOMatch(m.id, { date: e.target.value })}
                                    />
                                    <Input
                                      className={`${inputClass} h-8 text-xs`}
                                      placeholder="Terrain / salle"
                                      value={m.venue || ""}
                                      onChange={(e) => updateKOMatch(m.id, { venue: e.target.value })}
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      onClick={() => removeKOMatch(m.id)}
                                      className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"
                                      data-testid={`ko-remove-${m.label}`}
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </div>
                                  <div className="grid grid-cols-2 gap-2">
                                    {["home", "away"].map((side) => (
                                      <Select
                                        key={side}
                                        value={m[`${side}_override`] || "auto"}
                                        onValueChange={(v) =>
                                          updateKOMatch(m.id, {
                                            [`${side}_override`]: v === "auto" ? null : v,
                                          })
                                        }
                                      >
                                        <SelectTrigger
                                          className={`${inputClass} h-8 text-[11px] ${m[`${side}_override`] ? "border-[#D4AF37]/50" : ""}`}
                                          data-testid={`ko-${side}-override-${m.label}`}
                                        >
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#111216] border-white/10 text-white">
                                          <SelectItem value="auto">
                                            {side === "home" ? "Équipe 1 : auto" : "Équipe 2 : auto"}
                                          </SelectItem>
                                          {form.teams
                                            .filter((t) => t.name)
                                            .map((t) => (
                                              <SelectItem key={t.id} value={t.id}>
                                                {t.name}
                                                {t.group ? ` (${t.group})` : ""}
                                              </SelectItem>
                                            ))}
                                        </SelectContent>
                                      </Select>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <SectionTitle>Classement final</SectionTitle>
              <div className="space-y-3" data-testid="ranking-override-card">
                <p className="text-xs text-zinc-500 font-body">
                  Par défaut, le classement final (1er → 8e) est calculé
                  automatiquement depuis les phases finales. Activez la correction
                  manuelle pour fixer l&apos;ordre vous-même et ajouter des places
                  supplémentaires (9e, 10e…), sans rien recommencer.
                </p>
                {overrideList.length === 0 ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={startOverride}
                    data-testid="ranking-override-enable"
                    className="border-white/15 bg-transparent text-zinc-200 hover:bg-white/5 hover:text-white h-9 text-xs"
                  >
                    Corriger manuellement le classement
                  </Button>
                ) : (
                  <div className="space-y-2">
                    {overrideList.map((row, i) => (
                      <div key={i} className="flex items-center gap-1.5 sm:gap-2">
                        <div
                          className="font-display font-black w-9 text-right shrink-0"
                          style={{ color: i < 3 ? "#D4AF37" : "#71717A" }}
                        >
                          {i + 1}
                          <span className="text-[9px] align-top">{i === 0 ? "er" : "e"}</span>
                        </div>
                        <Select
                          value={row.team_id || ""}
                          onValueChange={(v) => setOverrideRow(i, v)}
                        >
                          <SelectTrigger
                            className={`${inputClass} h-9 text-xs flex-1 min-w-0`}
                            data-testid={`ranking-override-team-${i + 1}`}
                          >
                            <SelectValue placeholder="Choisir une équipe" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#111216] border-white/10 text-white">
                            {form.teams
                              .filter((t) => t.name)
                              .map((t) => (
                                <SelectItem key={t.id} value={t.id}>
                                  {t.name}
                                  {t.group ? ` (${t.group})` : ""}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => moveOverride(i, -1)}
                          disabled={i === 0}
                          className="h-9 w-8 p-0 text-zinc-500 hover:text-white hover:bg-white/5"
                          data-testid={`ranking-override-up-${i + 1}`}
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => moveOverride(i, 1)}
                          disabled={i === overrideList.length - 1}
                          className="h-9 w-8 p-0 text-zinc-500 hover:text-white hover:bg-white/5"
                          data-testid={`ranking-override-down-${i + 1}`}
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => removeOverrideRow(i)}
                          className="h-9 w-8 p-0 text-zinc-500 hover:text-red-400 hover:bg-red-500/10"
                          data-testid={`ranking-override-remove-${i + 1}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    ))}
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={addOverrideRow}
                        data-testid="ranking-override-add"
                        className="border-white/15 bg-transparent text-zinc-200 hover:bg-white/5 hover:text-white h-9 text-xs"
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une place
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={clearOverride}
                        data-testid="ranking-override-clear"
                        className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10 h-9 text-xs"
                      >
                        Revenir au calcul automatique
                      </Button>
                    </div>
                    <p className="text-[11px] text-zinc-600 font-body">
                      Ce classement remplace le classement automatique sur la page
                      publique. Pensez à enregistrer.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
          </div>

          <div className={tabClass("medias")}>
          <SectionTitle>Ordre des sections de la page</SectionTitle>
          <SectionOrderCard
            page="tournament"
            value={form.section_order}
            onChange={(v) => set("section_order", v)}
          />

          <SectionTitle>Vidéos de l'évènement</SectionTitle>
          <p className="text-zinc-500 font-body text-sm -mt-4">
            YouTube, Vimeo ou lien direct vers un fichier vidéo (.mp4, .webm).
            Les vidéos s'afficheront sur la page publique du tournoi.
          </p>
          <div className="space-y-3">
            {form.highlights.map((h, i) => (
              <div
                key={h.id || i}
                className="grid sm:grid-cols-[1fr,1.5fr,140px,auto] gap-2 items-end bg-[#0A0B0E] border border-white/5 rounded-md p-3"
                data-testid={`tournament-highlight-row-${i}`}
              >
                <Input
                  className={inputClass}
                  placeholder="Titre"
                  value={h.title || ""}
                  onChange={(e) => updateHighlight(i, { title: e.target.value })}
                />
                <Input
                  className={inputClass}
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={h.url || ""}
                  onChange={(e) => updateHighlight(i, { url: e.target.value })}
                />
                <Select
                  value={h.type || "youtube"}
                  onValueChange={(v) => updateHighlight(i, { type: v })}
                >
                  <SelectTrigger className={inputClass}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111216] border-white/10 text-white">
                    <SelectItem value="youtube">YouTube</SelectItem>
                    <SelectItem value="vimeo">Vimeo</SelectItem>
                    <SelectItem value="url">Fichier (URL)</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="ghost"
                  className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                  onClick={() => removeHighlight(i)}
                  data-testid={`remove-tournament-highlight-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              data-testid="add-tournament-highlight"
              onClick={addHighlight}
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Ajouter une vidéo
            </Button>
          </div>

          <SectionTitle>Photos</SectionTitle>
          <div className="space-y-3">
            {(form.gallery || []).map((g, i) => (
              <div
                key={g.id || i}
                className="grid sm:grid-cols-[auto,1fr,40px] gap-3 items-start bg-[#0A0B0E] border border-white/5 rounded-md p-3"
                data-testid={`t-gallery-${i}`}
              >
                <ImageInput
                  value={g.url || ""}
                  onChange={(v) => updateItem("gallery", i, { url: v })}
                  testId={`img-t-gallery-${i}`}
                  size="sm"
                  placeholder="URL photo"
                />
                <Input
                  className={inputClass}
                  placeholder="Légende (optionnelle)"
                  value={g.caption || ""}
                  onChange={(e) => updateItem("gallery", i, { caption: e.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => removeItem("gallery", i)}
                  className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                  data-testid={`t-remove-gallery-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              data-testid="t-add-gallery"
              onClick={() => addItem("gallery", { url: "", caption: "" })}
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Ajouter une photo
            </Button>
          </div>

          <SectionTitle>Partenaires / Sponsors</SectionTitle>
          <div className="space-y-3">
            {(form.sponsors || []).map((s, i) => (
              <div
                key={s.id || i}
                className="grid sm:grid-cols-[auto,1fr,1fr,40px] gap-3 items-start bg-[#0A0B0E] border border-white/5 rounded-md p-3"
                data-testid={`t-sponsor-${i}`}
              >
                <ImageInput
                  value={s.logo_url || ""}
                  onChange={(v) => updateItem("sponsors", i, { logo_url: v })}
                  testId={`img-t-sponsor-${i}`}
                  size="sm"
                  placeholder="URL logo"
                />
                <Input
                  className={inputClass}
                  placeholder="Nom du partenaire"
                  value={s.name || ""}
                  onChange={(e) => updateItem("sponsors", i, { name: e.target.value })}
                />
                <Input
                  className={inputClass}
                  placeholder="Site web (optionnel)"
                  value={s.website || ""}
                  onChange={(e) => updateItem("sponsors", i, { website: e.target.value })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => removeItem("sponsors", i)}
                  className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                  data-testid={`t-remove-sponsor-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              data-testid="t-add-sponsor"
              onClick={() => addItem("sponsors", { name: "", logo_url: "", website: "" })}
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Ajouter un partenaire
            </Button>
          </div>

          <SectionTitle>Meilleurs joueurs (sélection manuelle)</SectionTitle>
          <PlayerPicker
            players={allPlayers}
            selectedIds={form.top_player_ids}
            onToggle={togglePlayer}
            testIdPrefix="pick-player"
          />
          <Hint>
            Les joueurs sélectionnés ci-dessus sont cliquables (lien vers leur fiche).
            Ajoute ci-dessous un joueur SANS fiche : il s'affichera sans lien.
          </Hint>
          <div className="space-y-2">
            {(form.top_players_custom || []).map((p, i) => (
              <div
                key={p.id || i}
                className="bg-[#0A0B0E] border border-white/5 rounded-md p-3 grid sm:grid-cols-[1fr,1fr,120px,40px] gap-2 items-end"
                data-testid={`t-custom-player-${i}`}
              >
                <Field label="Nom du joueur" htmlFor={`t-custom-player-name-${i}`}>
                  <Input
                    id={`t-custom-player-name-${i}`}
                    className={inputClass}
                    placeholder="Prénom Nom"
                    value={p.name || ""}
                    onChange={(e) => updateItem("top_players_custom", i, { name: e.target.value })}
                    data-testid={`t-custom-player-name-input-${i}`}
                  />
                </Field>
                <Field label="Club / Équipe (facultatif)" htmlFor={`t-custom-player-club-${i}`}>
                  <Input
                    id={`t-custom-player-club-${i}`}
                    className={inputClass}
                    list="t-team-names"
                    placeholder="FC Exemple"
                    value={p.club || ""}
                    onChange={(e) => updateItem("top_players_custom", i, { club: e.target.value })}
                  />
                </Field>
                <Field label="Buts / Stat" htmlFor={`t-custom-player-stat-${i}`}>
                  <Input
                    id={`t-custom-player-stat-${i}`}
                    className={inputClass}
                    placeholder="5"
                    value={p.stat_value || ""}
                    onChange={(e) => updateItem("top_players_custom", i, { stat_value: e.target.value })}
                  />
                </Field>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => removeItem("top_players_custom", i)}
                  className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                  data-testid={`t-remove-custom-player-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                <div className="sm:col-span-4">
                  <Field label="Photo (facultatif)">
                    <ImageInput
                      value={p.photo_url}
                      onChange={(v) => updateItem("top_players_custom", i, { photo_url: v })}
                      testId={`t-custom-player-photo-${i}`}
                    />
                  </Field>
                </div>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              data-testid="t-add-custom-player"
              onClick={() => addItem("top_players_custom", { name: "", club: "", stat_value: "", photo_url: "" })}
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Ajouter un joueur sans fiche
            </Button>
          </div>

          <SectionTitle>Récompenses d'équipe</SectionTitle>
          <Hint>Équipe fair-play, meilleure défense, équipe révélation…</Hint>
          <div className="space-y-2">
            {(form.team_awards || []).map((a, i) => (
              <div
                key={a.id || i}
                className="bg-[#0A0B0E] border border-white/5 rounded-md p-3 grid sm:grid-cols-[1fr,1fr,1fr,40px] gap-2 items-end"
                data-testid={`t-team-award-${i}`}
              >
                <Field label="Titre" htmlFor={`t-team-award-title-${i}`}>
                  <Input
                    id={`t-team-award-title-${i}`}
                    className={inputClass}
                    list="team-award-suggestions"
                    placeholder="Équipe fair-play"
                    value={a.title || ""}
                    onChange={(e) => updateItem("team_awards", i, { title: e.target.value })}
                    data-testid={`t-team-award-title-input-${i}`}
                  />
                </Field>
                <Field label="Équipe" htmlFor={`t-team-award-team-${i}`}>
                  <Input
                    id={`t-team-award-team-${i}`}
                    className={inputClass}
                    list="t-team-names"
                    placeholder="Nom de l'équipe"
                    value={a.team_name || ""}
                    onChange={(e) => updateItem("team_awards", i, { team_name: e.target.value })}
                    data-testid={`t-team-award-team-input-${i}`}
                  />
                </Field>
                <Field label="Note (facultatif)" htmlFor={`t-team-award-note-${i}`}>
                  <Input
                    id={`t-team-award-note-${i}`}
                    className={inputClass}
                    placeholder="0 carton sur le tournoi"
                    value={a.note || ""}
                    onChange={(e) => updateItem("team_awards", i, { note: e.target.value })}
                  />
                </Field>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => removeItem("team_awards", i)}
                  className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                  data-testid={`t-remove-team-award-${i}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
                <div className="sm:col-span-4">
                  <Field label="Photo de l'équipe (facultatif)">
                    <ImageInput
                      value={a.photo_url}
                      onChange={(v) => updateItem("team_awards", i, { photo_url: v })}
                      testId={`t-team-award-photo-${i}`}
                    />
                  </Field>
                </div>
              </div>
            ))}
            <datalist id="team-award-suggestions">
              {["Équipe fair-play", "Meilleure attaque", "Meilleure défense", "Équipe révélation"].map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
            <datalist id="t-team-names">
              {(form.teams || []).map((t) => (
                <option key={t.id || t.name} value={t.name} />
              ))}
            </datalist>
            <Button
              type="button"
              variant="outline"
              data-testid="t-add-team-award"
              onClick={() => addItem("team_awards", { title: "", team_name: "", note: "" })}
              className="bg-transparent border-white/15 hover:bg-white/5 hover:border-white/40 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Ajouter une récompense d'équipe
            </Button>
          </div>
          </div>

          <div className="sticky bottom-4 z-10">
            <div className="bg-[#0A0B0E]/90 backdrop-blur-xl border border-white/10 rounded-xl p-3 flex items-center justify-between gap-3">
              {isEdit ? (
                <Button
                  type="button"
                  variant="ghost"
                  data-testid="delete-tournament"
                  onClick={onDelete}
                  className="text-red-400 hover:bg-red-500/10 hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                </Button>
              ) : (
                <span />
              )}
              <Button
                type="submit"
                disabled={saving}
                data-testid="save-tournament"
                className="text-black rounded-md px-6 h-11 font-body font-medium hover:opacity-90"
                style={{ backgroundColor: form.primary_color || "#D4AF37" }}
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Enregistrement..." : isEdit ? "Mettre à jour" : "Créer le tournoi"}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
